import CryptoJS from 'crypto-js';

import moment from 'moment';
import { campaignTypes } from './constants';

const numbers = '0123456789';
const smallLetters = 'abcdefghijklmnopqrstuvwxyz';
const capitalLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const randomString = (length, chars) => {
  let result = '';
  for (let i = length; i > 0; --i) {
    result += chars[Math.round(Math.random() * (chars.length - 1))];
  }
  return result;
};

// Partnerships
export const hasMoneyInType = (campaignType) =>
  [campaignTypes.MONEY, campaignTypes.MONEY_PRODUCT, campaignTypes.MONEY_EXPERIENCE].includes(
    campaignType
  );

// General
export const generateSecretKey = () => {
  const sKey = randomString(6, `${smallLetters}${numbers}${capitalLetters}`);
  return randomString(20, sKey + moment().format('YYYYDDMMHHmmss'));
};

export const encodeJSONToBase64 = (jsonObject) => {
  const jsonString = JSON.stringify(jsonObject); // Convert JSON to string

  const cryptString = CryptoJS.enc.Utf8.parse(jsonString);
  const cryptKey = CryptoJS.enc.Hex.parse(import.meta.env.VITE_APP_PARAMS_SECURE_KEY);

  // Encrypt and return base64 string
  const encrypted = CryptoJS.AES.encrypt(cryptString, cryptKey, {
    iv: CryptoJS.enc.Hex.parse('00000000000000000000000000000000'),
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  });

  return encodeURIComponent(encrypted.ciphertext.toString(CryptoJS.enc.Base64));
};

export const decodeBase64ToJSON = (encodedString) => {
  try {
    const cryptKey = CryptoJS.enc.Hex.parse(import.meta.env.VITE_APP_PARAMS_SECURE_KEY);

    const decoded = CryptoJS.AES.decrypt(decodeURIComponent(encodedString), cryptKey, {
      iv: CryptoJS.enc.Hex.parse('00000000000000000000000000000000'),
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });

    const decryptedString = decoded.toString(CryptoJS.enc.Utf8);

    // Parse the decrypted string back to JSON
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

export const numberFormatterWithCurrency = ({ amount, countryCode, currencyCode }) => {
  if (currencyCode === 'EUR') {
    let newNumber = new Intl.NumberFormat(countryCode).format(parseFloat(amount));
    newNumber = [...newNumber]
      .map((char) => {
        if (char === '.') {
          return ',';
        }
        if (char === ',') {
          return '.';
        }

        return char;
      })
      .join('');
    return `${newNumber} €`;
  }
  const formattedNumber = new Intl.NumberFormat(countryCode, {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'symbol'
  }).format(parseFloat(amount));

  return formattedNumber;
};

// Common Emails
const EMAIL_ADDRESS = {
  APPCHAT_MAIL: 'ana.mileth@luagroup.com',
  BILLING_MAIL_EN: 'billing@luagroup.com',
  BILLING_MAIL_ES: 'facturacion@luagroup.com',
  COMMUNITY_MAIL_EN: 'community@luagroup.com',
  COMMUNITY_MAIL_ES: 'comunidad@luagroup.com',
  INFO_MAIL: 'info@luagroup.com',
  METRICS_MAIL: 'metrics@luagroup.com',
  NOREPLY_MAIL: 'no-reply@luagroup.com',
  FORGOT_PASSWORD_MAIL: 'spain@luagroup.com',
  AUSTRALIA_NUMU_EMAIL: 'australia@luagroup.com',
  SPAIN_NUMU_EMAIL: 'spain@luagroup.com'
};

export function getEmailAddress(emailAddress) {
  return EMAIL_ADDRESS[emailAddress];
}

// Normalize email inputs by trimming whitespace and collapsing comma-separated values
export const trimEmailValue = (email = '') => (typeof email === 'string' ? email.trim() : email);

export const normalizeEmailList = (value) => {
  if (value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    return value
      .map((entry) => trimEmailValue(entry))
      .filter(Boolean)
      .join(',');
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => trimEmailValue(entry))
      .filter(Boolean)
      .join(',');
  }

  return value;
};

export const numuDetailsPTE = {
  fullName: 'Numu Pte. Ltd',
  address: '32 Pekin Street, #05-01, Far East Square, 048762, Singapore',
  bankAccount: 'DBS Bank',
  IBAN: '027-908565-7',
  CIF: '202003428H',
  swiftCode: 'DBSSSGSG'
};

export const numuDetails = {
  fullName: 'NumuCorp Spain S.L.',
  address: 'Calle Jacometrezo 15, 3D, 28013, Madrid, Madrid',
  bankAccount: 'Banco Santander',
  IBAN: 'ES57 0049 7699 1921 1000 2119',
  CIF: 'B90429044',
  swiftCode: 'BSCHESMMXXX'
};

export const numuDetailsUSD = {
  fullName: 'NumuCorp Spain S.L.',
  address: 'Calle Jacometrezo 15, 3D, 28013, Madrid, Madrid',
  bankAccount: 'Banco Santander',
  IBAN: 'ES06 0049 7699 1228 1001 8236',
  CIF: 'B90429044',
  swiftCode: 'BSCHESMMXXX'
};

export const NUMU_REGISTERED_COMPANY_SPAIN_SL = 'Numucorp Spain SL';
export const NUMU_REGISTERED_COMPANY_SINGAPORE_PTE = 'Numu Pte Ltd';

export const getNumuInvoicingRegisteredCompanyName = (issuedFrom) =>
  issuedFrom === 'numu_pte' ? NUMU_REGISTERED_COMPANY_SINGAPORE_PTE : NUMU_REGISTERED_COMPANY_SPAIN_SL;

export const getNumuDetailsForIssuedFrom = (issuedFrom = 'numu_sl') =>
  issuedFrom === 'numu_pte' ? numuDetailsPTE : numuDetails;

export const getContractIssuedFromTemplateValues = (issuedFrom = 'numu_sl') => {
  const details = getNumuDetailsForIssuedFrom(issuedFrom);
  return {
    issued_from: details.fullName,
    lua_billing_address: details.address,
    lua_tax_id: details.CIF
  };
};

// generate hash of string
// use the cryptoJS to generate a hash of the string
export const generateUniqueStr = (str = '') => {
  const timestamp = moment().unix();
  return CryptoJS.MD5(str + timestamp).toString();
};

export const formatCommentWithLinks = (text) => {
  if (!text) return null;
  const urlRegex = /((https?:\/\/)|(www\.)|^)[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+([/?].*)?/gi;
  const nodes = [];
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(<span key={`text-${match.index}`}>{text.substring(lastIndex, match.index)}</span>);
    }

    let url = match[0];
    if (url.startsWith('www.')) {
      url = `http://${url}`;
    }

    nodes.push(
      <a
        key={`link-${match.index}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-blue-60 cursor-pointer text-blue-50 underline"
        onClick={(event) => event.stopPropagation()}
      >
        {match[0]}
      </a>
    );

    lastIndex = urlRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(<span key={`text-${lastIndex}`}>{text.substring(lastIndex)}</span>);
  }

  return nodes.length > 0 ? nodes : text;
};
